import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, faClock, faUsers, faPhone, faEnvelope, faUser, 
  faCommentDots, faMapMarkerAlt, faGift, faTimes, faCheck, 
  faHeart, faCamera, faMusic, faCake, faWineGlass, faStar 
} from '@fortawesome/free-solid-svg-icons';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import axios from 'axios';

const Reservas = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Estados básicos
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const [step, setStep] = useState(1);
  const [currentSubStep, setCurrentSubStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para datos del backend
  const [occasions, setOccasions] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Datos mock para ocasiones
      setOccasions([
        { id: 'birthday', name: 'Cumpleaños', display: 'Cumpleaños', emoji: '🎂' },
        { id: 'anniversary', name: 'Aniversario', display: 'Aniversario', emoji: '💕' },
        { id: 'romantic', name: 'Romántica', display: 'Romántica', emoji: '🌹' },
        { id: 'business', name: 'Negocios', display: 'Negocios', emoji: '💼' },
        { id: 'celebration', name: 'Celebración', display: 'Celebración', emoji: '🎉' },
        { id: 'casual', name: 'Casual', display: 'Casual', emoji: '😊' }
      ]);

      // Datos mock para zonas
      setZones([
        { 
          id: 'salon', 
          name: 'Salón Principal', 
          description: 'Ambiente elegante con vista a la cocina abierta', 
          capacity: 60, 
          fee: 0 
        },
        { 
          id: 'terraza', 
          name: 'Patio interior', 
          description: 'Espacio al aire libre con plantas tropicales', 
          capacity: 30, 
          fee: 0 
        },
        { 
          id: 'vip', 
          name: 'VIP', 
          description: 'Área privada e íntima para ocasiones especiales', 
          capacity: 20, 
          fee: 0 
        }
      ]);
      
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Horarios disponibles (12:00 PM - 12:00 AM)
  const timeSlots = [
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
    '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
  ];

  // Función para verificar si una fecha es hoy
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Función para obtener horarios disponibles según la fecha
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return timeSlots;

    if (isToday(selectedDate)) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();

      // Filtrar horarios que sean al menos 1 hora después de ahora
      return timeSlots.filter(slot => {
        const [hours, minutes] = slot.split(':').map(Number);
        const slotTotalMinutes = hours * 60 + minutes;
        const currentTotalMinutes = currentHour * 60 + currentMinutes;

        // Requiere al menos 60 minutos de anticipación
        return slotTotalMinutes >= currentTotalMinutes + 60;
      });
    }

    return timeSlots;
  };

  const handleDateSelect = (date) => {
    // Permitir desde hoy en adelante (no solo mañana)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date >= today) {
      setSelectedDate(date);
      // Si cambia la fecha a hoy y la hora seleccionada ya no está disponible, limpiarla
      if (isToday(date) && selectedTime) {
        const availableSlots = timeSlots.filter(slot => {
          const now = new Date();
          const [hours, minutes] = slot.split(':').map(Number);
          const slotTotalMinutes = hours * 60 + minutes;
          const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();
          return slotTotalMinutes >= currentTotalMinutes + 60;
        });
        if (!availableSlots.includes(selectedTime)) {
          setSelectedTime(null);
        }
      }
    }
  };

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Format date correctly - use local date to avoid timezone issues
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const reservationData = {
      customer: {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phone.startsWith('+') ? customerInfo.phone : '+53' + customerInfo.phone.replace(/\s+/g, ''),
        email: customerInfo.email || null
      },
      reservation: {
        reservation_date: formattedDate,
        reservation_time: selectedTime,
        guests: partySize,
        zone: selectedZone,
        occasion: selectedOccasion,
        special_requests: customerInfo.specialRequests || null
      },
      upselling_items: []
    };

    // Retry logic - try up to 3 times with longer timeouts for Cuba's slow connections
    const maxRetries = 3;
    const timeouts = [30000, 45000, 60000]; // 30s, 45s, 60s - generous for slow Cuban internet
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Intento ${attempt} de ${maxRetries} (timeout: ${timeouts[attempt-1]/1000}s)...`);

        const response = await axios.post(`${backendUrl}/api/reservations/submit`, reservationData, {
          timeout: timeouts[attempt - 1]
        });

        if (response.data.success) {
          setStep(4); // Success step
          setIsSubmitting(false);
          return; // Exit on success
        } else {
          throw new Error(response.data.message || 'Error procesando reserva');
        }
      } catch (error) {
        console.error(`Error en intento ${attempt}:`, error);
        lastError = error;

        // If it's not a network/timeout error, don't retry
        if (error.response?.status === 422 || error.response?.status === 400) {
          break;
        }

        // Wait before retry (except on last attempt) - longer wait for slow connections
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt)); // 2s, 4s
        }
      }
    }

    // All retries failed - show error
    let errorMessage = 'Error enviando la reserva. ';

    if (lastError?.code === 'ECONNABORTED' || lastError?.message?.includes('timeout')) {
      errorMessage += 'La conexión tardó demasiado. Por favor intenta de nuevo o contáctanos por WhatsApp al +53 6351 8498.';
    } else if (lastError?.code === 'ERR_NETWORK' || !navigator.onLine) {
      errorMessage += 'Problema de conexión a internet. Por favor verifica tu conexión e intenta de nuevo, o contáctanos por WhatsApp al +53 6351 8498.';
    } else if (lastError?.response?.status === 422) {
      errorMessage += 'Por favor verifica que la fecha seleccionada sea válida (debe ser una fecha futura).';
    } else {
      errorMessage += 'Por favor intenta de nuevo o contáctanos directamente por WhatsApp al +53 6351 8498.';
    }

    alert(errorMessage);
    setIsSubmitting(false);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const canProceedToStep2 = selectedDate && selectedTime && partySize && selectedZone;
  const canProceedToStep3 = customerInfo.firstName && customerInfo.lastName && customerInfo.phone && selectedOccasion;
  const canProceedToStep4 = true; // Always can review after step 3

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" style={{ backgroundColor: '#000000' }}>
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 border-4 border-white/30 border-t-white rounded-full mx-auto mb-4"></div>
          <p className="text-white">Cargando datos de reservas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container-custom max-w-7xl px-4 pt-40 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 min-h-[calc(100vh-200px)]">
          {/* Columna Izquierda - Imagen con Logo */}
          <div className="relative bg-gradient-to-br from-gray-800 to-black rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl h-80 lg:h-auto">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-50"
              style={{
                backgroundImage: `url('https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20grande.JPG')`
              }}
            ></div>
            <div className="relative z-10 flex flex-col items-center justify-end w-full h-full p-6 lg:p-12 text-white">
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-24 lg:h-32 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>

          {/* Columna Derecha - Sistema de Reservas */}
          <div className="flex flex-col justify-center">
            {/* Header del formulario */}
            <div className="text-center mb-20">
              <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
                Reservar <span className="text-green-600 font-normal italic">Mesa</span>
              </h2>
              <p className="font-sans text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
                Asegura tu lugar en ADASTRA y vive una experiencia gastronómica única.
              </p>
            </div>

            {/* Contenido del formulario */}
            <div className="backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden flex-1 border border-gray-300 bg-white">
              
          {/* Step 1: Date, Time, Zone Selection */}
          {step === 1 && (
            <div className="p-8 h-full flex flex-col">
              <div className="mb-6">
                <h3 className="font-sans text-2xl font-light mb-2 text-black">Hacer Reserva</h3>
                <p className="font-sans text-gray-600 text-sm">Completa los detalles para tu experiencia</p>
              </div>

              <div className="flex-1 flex flex-col justify-center">
                {/* Sub-step indicator */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-sans text-sm font-medium text-gray-600">Paso {currentSubStep} de 5</span>
                    <div className="flex space-x-2">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full transition-all duration-300 ${
                          i <= currentSubStep 
                            ? 'bg-gradient-to-r from-black to-gray-700 shadow-lg' 
                            : 'bg-gray-200'
                        }`} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sub-step 1: Calendar */}
                {currentSubStep === 1 && (
                  <div className="text-center flex flex-col items-center justify-center">
                    <h4 className="font-sans text-2xl font-light mb-6 flex items-center justify-center text-black">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-black" />
                      Selecciona la Fecha
                    </h4>
                    <div className="bg-white rounded-2xl p-6 w-fit mx-auto shadow-xl border border-gray-200">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          handleDateSelect(date);
                          if (date) setCurrentSubStep(2);
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                {/* Sub-step 2: Time */}
                {currentSubStep === 2 && (
                  <div className="text-center">
                    <h4 className="font-sans text-lg font-light mb-3 flex items-center justify-center text-black">
                      <FontAwesomeIcon icon={faClock} className="mr-2 text-black" />
                      ¿A qué hora?
                    </h4>
                    {getAvailableTimeSlots().length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
                        {getAvailableTimeSlots().map(time => (
                          <button
                            key={time}
                            onClick={() => {
                              setSelectedTime(time);
                              setCurrentSubStep(3);
                            }}
                            className={`font-sans p-3 rounded-lg border-2 transition-all font-medium ${
                              selectedTime === time
                                ? 'border-black bg-black text-white'
                                : 'border-gray-300 text-black hover:border-black hover:bg-black/5'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-500 mb-2">No hay horarios disponibles para hoy.</p>
                        <p className="text-sm text-gray-400">Por favor selecciona otra fecha.</p>
                      </div>
                    )}
                    <button onClick={() => setCurrentSubStep(1)} className="font-sans mt-4 text-sm text-gray-500 hover:text-black">
                      ← Cambiar fecha
                    </button>
                  </div>
                )}

                {/* Sub-step 3: Party Size */}
                {currentSubStep === 3 && (
                  <div className="text-center">
                    <h4 className="font-sans text-lg font-light mb-3 flex items-center justify-center text-black">
                      <FontAwesomeIcon icon={faUsers} className="mr-2 text-black" />
                      ¿Cuántas personas?
                    </h4>
                    <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto">
                      {[2, 4, 6, 8, 10, 12, 15, 20].map(size => (
                        <button
                          key={size}
                          onClick={() => {
                            setPartySize(size);
                            setCurrentSubStep(4);
                          }}
                          className={`font-sans p-3 rounded-lg border-2 transition-all font-medium ${
                            partySize === size
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 text-black hover:border-black hover:bg-black/5'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setCurrentSubStep(2)} className="font-sans mt-4 text-sm text-gray-500 hover:text-black">
                      ← Cambiar hora
                    </button>
                  </div>
                )}

                {/* Sub-step 4: Zone */}
                {currentSubStep === 4 && (
                  <div className="text-center">
                    <h4 className="font-sans text-lg font-light mb-3 flex items-center justify-center text-black">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-black" />
                      ¿Dónde prefieres sentarte?
                    </h4>
                    <div className="space-y-3 max-w-sm mx-auto">
                      {zones.map(zone => (
                        <button
                          key={zone.id}
                          onClick={() => {
                            setSelectedZone(zone.id);
                            setCurrentSubStep(5);
                          }}
                          className={`font-sans w-full p-3 rounded-lg border-2 transition-all text-left ${
                            selectedZone === zone.id
                              ? 'border-black bg-black text-white'
                              : 'border-gray-300 text-black hover:border-black hover:bg-black/5'
                          }`}
                        >
                          <div className="font-medium">{zone.name}</div>
                          <div className={`text-sm ${selectedZone === zone.id ? 'text-gray-200' : 'text-black'}`}>
                            {zone.description}
                          </div>
                        </button>
                      ))}
                    </div>
                    <button onClick={() => setCurrentSubStep(3)} className="font-sans mt-4 text-sm text-gray-500 hover:text-black">
                      ← Cambiar personas
                    </button>
                  </div>
                )}

                {/* Sub-step 5: Occasion */}
                {currentSubStep === 5 && (
                  <div className="text-center">
                    <h4 className="font-sans text-lg font-light mb-3 flex items-center justify-center text-black">
                      <FontAwesomeIcon icon={faGift} className="mr-2 text-black" />
                      ¿Alguna ocasión especial?
                    </h4>
                    <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mb-4">
                      {occasions.map(occasion => {
                        // Mapear ocasiones a iconos de FontAwesome
                        const getIcon = (name) => {
                          switch(name.toLowerCase()) {
                            case 'cumpleaños': case 'birthday': return faCake;
                            case 'aniversario': case 'anniversary': return faHeart;
                            case 'celebración': case 'celebration': return faStar;
                            case 'romántica': case 'romantic': return faWineGlass;
                            case 'negocios': case 'business': return faUser;
                            default: return faGift;
                          }
                        };

                        return (
                          <button
                            key={occasion.id}
                            onClick={() => setSelectedOccasion(occasion.id)}
                            className={`font-sans p-3 rounded-lg border-2 transition-all text-center ${
                              selectedOccasion === occasion.id
                                ? 'border-black bg-black text-white'
                                : 'border-gray-300 text-black hover:border-black hover:bg-black/5'
                            }`}
                          >
                            <FontAwesomeIcon 
                              icon={getIcon(occasion.name)} 
                              className={`text-xl mb-2 ${
                                selectedOccasion === occasion.id ? 'text-white' : 'text-black'
                              }`} 
                            />
                            <div className={`font-medium text-sm ${
                              selectedOccasion === occasion.id ? 'text-white' : 'text-black'
                            }`}>{occasion.display}</div>
                          </button>
                        );
                      })}
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => setStep(2)}
                        className="border-2 border-gray-900 text-gray-900 font-semibold px-8 py-4 rounded-lg hover:bg-gray-900 hover:text-white transition-colors text-base inline-flex items-center justify-center space-x-2 mx-auto"
                      >
                        <span>CONTINUAR</span>
                        <span>→</span>
                      </button>
                      <button onClick={() => setCurrentSubStep(4)} className="font-sans text-sm text-gray-500 hover:text-black">
                        ← Cambiar zona
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Customer Information & Occasion */}
          {step === 2 && (
            <div className="p-8 space-y-8">
              <h2 className="font-sans text-2xl font-light text-gray-900 text-center mb-8">
                Información Personal y <span className="text-black font-normal italic">Ocasión</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-black" />
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={customerInfo.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="modern-input w-full"
                    placeholder="Tu nombre"
                    required
                  />
                </div>

                <div>
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-black" />
                    Apellido *
                  </label>
                  <input
                    type="text"
                    value={customerInfo.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="modern-input w-full"
                    placeholder="Tu apellido"
                    required
                  />
                </div>

                <div>
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faPhone} className="mr-2 text-black" />
                    WhatsApp *
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="modern-input w-full"
                    placeholder="53 7 123 4567"
                    required
                  />
                </div>

                <div>
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-black" />
                    Email (Opcional)
                  </label>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="modern-input w-full"
                    placeholder="tu@email.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faHeart} className="mr-2 text-black" />
                    ¿Cuál es la ocasión? *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {occasions.map(occasion => (
                      <button
                        key={occasion.id}
                        onClick={() => setSelectedOccasion(occasion.id)}
                        className={`font-sans p-3 rounded-xl border-2 transition-colors font-medium text-sm ${
                          selectedOccasion === occasion.id
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 text-black hover:border-black hover:bg-black/10'
                        }`}
                      >
                        {occasion.display}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block font-sans font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-black" />
                    Solicitudes Especiales
                  </label>
                  <textarea
                    value={customerInfo.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    className="modern-input w-full h-24 resize-none"
                    placeholder="Alergias, preferencias de mesa, decoración especial..."
                  />
                </div>
              </div>

              <div className="flex justify-center space-x-4 pt-6">
                <button
                  onClick={() => setStep(1)}
                  className="border-2 border-gray-300 text-gray-600 font-semibold px-6 py-3 rounded-lg hover:border-gray-600 hover:text-gray-900 transition-colors text-base inline-flex items-center justify-center space-x-2"
                >
                  <span>←</span>
                  <span>VOLVER</span>
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                  className={`border-2 border-gray-900 text-gray-900 font-semibold px-8 py-4 rounded-lg hover:bg-gray-900 hover:text-white transition-colors text-base inline-flex items-center justify-center space-x-2 ${
                    !canProceedToStep3 ? 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-gray-900' : ''
                  }`}
                >
                  <span>CONTINUAR</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Upselling Selection */}
          {/* Step 3: Review and Confirm */}
          {step === 3 && (
            <div className="p-8 space-y-8">
              <h2 className="font-sans text-2xl font-semibold text-center mb-8">
                Confirmar Reserva
              </h2>

              <div className="bg-white rounded-2xl p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-[#000000] mb-2">Fecha y Hora</h4>
                    <p className="font-medium">{formatDate(selectedDate)}</p>
                    <p className="text-[#626b79]">{selectedTime}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-[#000000] mb-2">Detalles</h4>
                    <p><strong>Personas:</strong> {partySize}</p>
                    <p><strong>Zona:</strong> {zones.find(z => z.id === selectedZone)?.name}</p>
                    <p><strong>Ocasión:</strong> {occasions.find(o => o.id === selectedOccasion)?.display}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-[#000000] mb-2">Contacto</h4>
                    <p className="font-medium">{customerInfo.firstName} {customerInfo.lastName}</p>
                    <p className="text-[#626b79]">{customerInfo.phone}</p>
                    {customerInfo.email && <p className="text-[#626b79]">{customerInfo.email}</p>}
                  </div>

                  {customerInfo.specialRequests && (
                    <div className="col-span-full">
                      <h4 className="font-medium text-[#000000] mb-2">Solicitudes Especiales</h4>
                      <p className="text-[#626b79]">{customerInfo.specialRequests}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setStep(2)}
                  className="border-2 border-gray-300 text-gray-600 font-semibold px-6 py-3 rounded-lg hover:border-gray-600 hover:text-gray-900 transition-colors text-base inline-flex items-center justify-center space-x-2"
                >
                  <span>←</span>
                  <span>EDITAR</span>
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="border-2 border-gray-900 text-gray-900 font-semibold px-8 py-4 rounded-lg hover:bg-gray-900 hover:text-white transition-colors text-base inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div>
                      <span>PROCESANDO...</span>
                    </>
                  ) : (
                    <>
                      <span>CONFIRMAR RESERVA</span>
                      <FontAwesomeIcon icon={faCheck} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center space-y-8 py-8">
              <div className="w-20 h-20 bg-[#000000] rounded-full flex items-center justify-center mx-auto">
                <span className="text-white text-3xl">✓</span>
              </div>
              
              <div>
                <h2 className="font-sans text-3xl font-bold text-[#000000] mb-4">
                  Reserva enviada
                </h2>
                <p className="subtitle max-w-md mx-auto">
                  Hemos recibido tu reserva para tu {occasions.find(o => o.id === selectedOccasion)?.display.toLowerCase()}. 
                  Recibirás una confirmación por WhatsApp en breve. Si no recibes la confirmación, por favor contáctanos directamente.
                </p>
              </div>

              <div className="bg-[#000000]/10 rounded-2xl p-8 max-w-md mx-auto">
                <h4 className="font-medium mb-4">Detalles de tu Reserva</h4>
                <div className="space-y-2 text-sm">
                  <p><strong>Fecha:</strong> {formatDate(selectedDate)}</p>
                  <p><strong>Hora:</strong> {selectedTime}</p>
                  <p><strong>Personas:</strong> {partySize}</p>
                  <p><strong>Zona:</strong> {zones.find(z => z.id === selectedZone)?.name}</p>
                  <p><strong>Ocasión:</strong> {occasions.find(o => o.id === selectedOccasion)?.display}</p>
                  <p><strong>WhatsApp:</strong> {customerInfo.phone}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 pt-4">
                <a
                  href="https://wa.me/5363518498"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-gray-900 text-gray-900 font-semibold px-8 py-4 rounded-lg hover:bg-gray-900 hover:text-white transition-colors text-base inline-flex items-center justify-center space-x-2"
                >
                  <FontAwesomeIcon icon={faPhone} />
                  <span>Contactar</span>
                </a>
                <a 
                  href="/"
                  className="btn-adastra-primary inline-block text-center"
                >
                  Volver al Inicio
                </a>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="bg-black py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-green-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-400/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container-custom max-w-6xl mx-auto px-6 relative z-10">
          
          {/* Contact Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            
            {/* Contacto */}
            <div className="text-center group">
              <div className="flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faPhone} className="text-3xl text-white" />
              </div>
              <h3 className="font-sans text-2xl font-light text-white mb-4">Contáctanos</h3>
              <p className="font-sans text-lg text-gray-200 mb-2 font-medium">+53 6351 8498</p>
              <p className="font-sans text-sm text-gray-400 uppercase tracking-wider">Lun - Dom: 12:00 PM - 12:00 AM</p>
            </div>

            {/* Ubicación */}
            <div className="text-center group">
              <div className="flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-3xl text-white" />
              </div>
              <h3 className="font-sans text-2xl font-light text-white mb-4">Ubicación</h3>
              <p className="font-sans text-lg text-gray-200 mb-1 font-medium">Obispo 255 entre Cuba y Aguiar</p>
              <p className="font-sans text-lg text-gray-200 mb-2 font-medium">La Habana</p>
            </div>

            {/* Experiencia */}
            <div className="text-center group">
              <div className="flex items-center justify-center mx-auto mb-6">
                <FontAwesomeIcon icon={faWineGlass} className="text-3xl text-white" />
              </div>
              <h3 className="font-sans text-2xl font-light text-white mb-4">Experiencia</h3>
              <p className="font-sans text-lg text-gray-200 mb-1 font-medium">Gastronomía única</p>
              <p className="font-sans text-sm text-gray-400 uppercase tracking-wider">Momentos inolvidables</p>
            </div>
            
          </div>

          {/* Separador decorativo */}
          <div className="flex items-center justify-center mb-16">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-green-400/60 to-transparent"></div>
            <div className="mx-6 text-green-400 text-2xl">✦</div>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-green-400/60 to-transparent"></div>
          </div>

          {/* Call to Action */}
          <div className="text-center px-4">
            <h3 className="font-sans text-2xl md:text-5xl font-light text-white leading-tight mb-6">
              ¿Listo para una experiencia <span className="text-green-400 font-normal italic">única?</span>
            </h3>
            <p className="font-sans text-base md:text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl mx-auto">
              Reserva tu mesa y descubre por qué ADASTRA es el destino gastronómico 
              más exclusivo de La Habana.
            </p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white hover:bg-gray-100 text-black font-semibold px-10 py-4 rounded-full transition-all duration-300 inline-flex items-center space-x-3 hover:scale-105 shadow-lg"
            >
              <span>Hacer Reserva Ahora</span>
              <div className="w-6 h-6 bg-black/10 rounded-full flex items-center justify-center">
                <span className="text-sm">↑</span>
              </div>
            </button>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Reservas;